/**
 * CR500/CR305: HeadlineBlock
 * 
 * Renders the deal headline/mechanic text.
 * Extracted from DealGiftCard for v2 block-based rendering.
 * 
 * CR305: Uses centralized display helpers to clean brand names.
 */

import React from "react";
import type { DealCardBlockProps } from "@/lib/deal-cards/specV2/types";
import type { Deal } from "@/lib/types/deals";
import { cn } from "@/lib/utils";
import {
  getHeadlineMechanic,
  getDiscountDisplayStrings,
  isBonusPercentBackDeal,
} from "@/lib/types/deals";
import { getDealType } from "@/lib/deals/getDealType";
import { 
  isProbablyBadBrand, 
  stripNumericPrefix,
} from "@/lib/deals/dealCardDisplay";

interface HeadlineBlockProps extends DealCardBlockProps<Deal> {
  /** Optional headline clamp class from density settings */
  clampClass?: string;
}

export const HeadlineBlock: React.FC<HeadlineBlockProps> = ({
  deal,
  block,
  context,
  clampClass = "line-clamp-2",
}) => {
  // Don't render if block is disabled
  if (!block.enabled) {
    return null;
  }

  const { category: dealCategory } = getDealType(deal);
  
  // CR305: Clean brand names using centralized helpers
  const cleanBrand = (brand: string | null | undefined): string | null => {
    if (!brand) return null;
    if (isProbablyBadBrand(brand)) {
      return stripNumericPrefix(brand);
    }
    return brand;
  };
  
  // CR412: Generate structured BONUS headline using issuer name (no retailer in headline)
  const generateGiftCardBonusHeadline = (): string | null => {
    // CR412: Prefer issuer_display_name (clean, from merchants join), then field-level brands
    const issuerName = deal.issuer_display_name;
    const rawGcBrand = deal.purchase_gift_card_brand ?? deal.gift_card_brand;
    const gcBrand = issuerName || cleanBrand(rawGcBrand);
    
    const spend = deal.spend_amount ?? deal.qualifying_spend_cad;
    const bonus = deal.bonus_amount ?? deal.bonus_value_cad;
    
    if (!gcBrand) return null;
    
    const spendPart = spend ? `$${spend} ` : "";
    const bonusPart = bonus ? `$${bonus} ` : "";
    
    // CR412: No retailer in headline — retailer goes in condition line only
    return `Buy a ${spendPart}${gcBrand} gift card, get a ${bonusPart}bonus gift card.`;
  };

  // Determine headline: discount-specific > gift card bonus > general mechanic
  const headlineMechanic = getHeadlineMechanic(deal);
  const discountDisplay = dealCategory === "DISCOUNT" ? getDiscountDisplayStrings(deal) : null;
  const giftCardBonusHeadline = dealCategory === "BONUS" && (deal.purchase_gift_card_brand || deal.gift_card_brand)
    ? generateGiftCardBonusHeadline()
    : null;
  
  const displayHeadline = discountDisplay?.headline || giftCardBonusHeadline || headlineMechanic;

  if (!displayHeadline) {
    return null;
  }

  return (
    <div 
      className="mt-1 relative z-10 flex-shrink-0" 
      data-block="headline"
      data-block-id={block.id}
      data-component-key="headline"
    >
      <p 
        className={cn(
          "text-sm md:text-base font-medium leading-snug",
          clampClass
        )}
        style={{
          // CR307: Consume theme CSS vars for text styling
          color: "var(--wi-body-text, currentColor)",
          fontFamily: "var(--wi-heading-font, inherit)",
        }}
      >
        {displayHeadline}
      </p>
      
      {/* CR061: Subtext for BONUS % back deals */}
      {isBonusPercentBackDeal(deal) && deal.discount_percent && (
        <p className="text-xs text-muted-foreground mt-1">
          {deal.discount_percent}% back in a digital gift card
        </p>
      )}
    </div>
  );
};

export default HeadlineBlock;
